/* PUTAOYAO navigation transition v7 — native cross-document transitions + shared product imagery + bounded prefetch. */
(() => {
  if (window.pyPageTransition) return;

  const KEY = 'py-page-transition';
  const MAX_PREFETCH = 5;
  const HOVER_DELAY = 120;
  const prefetched = new Set();
  let hoverTimer = 0;
  let hoverAnchor = null;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const constrained = !!(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const nativeTransition = !!(window.CSS && CSS.supports && CSS.supports('view-transition-name: root'));

  function markNavigation() {
    try { sessionStorage.setItem(KEY, '1'); } catch {}
  }

  function sameOriginUrl(raw) {
    let url;
    try { url = new URL(raw, location.href); } catch { return null; }
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return null;
    return url;
  }

  // Used by sort/login/localization helpers that navigate via JS.
  // Mark the destination for fade-in, then navigate immediately.
  function navigate(raw) {
    const url = sameOriginUrl(raw);
    if (!url) return false;
    markNavigation();
    location.assign(url.href);
    return true;
  }

  // Used by localization-form.js. No fade-out and no artificial delay.
  function submitForm(form, submitter = null) {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = sameOriginUrl(form.action || location.href);
    if (!action) return false;

    markNavigation();

    let injected = null;
    if (submitter?.name) {
      injected = document.createElement('input');
      injected.type = 'hidden';
      injected.name = submitter.name;
      injected.value = submitter.value || '';
      form.appendChild(injected);
    }

    try { HTMLFormElement.prototype.submit.call(form); }
    finally { injected?.remove(); }
    return true;
  }

  function getAnchor(target) {
    return target?.closest?.('a[href]') || null;
  }

  function armProductTransition(anchor) {
    if (!nativeTransition || reducedMotion.matches || !anchor) return;
    const card = anchor.closest?.('[data-py-product-transition]');
    if (!card) return;
    const name = card.dataset.pyProductTransition;
    const visual = card.querySelector?.('[data-py-product-transition-visual]');
    if (!name || !visual) return;
    visual.style.viewTransitionName = name;
  }

  function destination(anchor) {
    if (!anchor || anchor.hasAttribute('download')) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    if (anchor.dataset.pyNoPrefetch != null || anchor.dataset.noPrefetch != null) return null;

    const raw = (anchor.getAttribute('href') || '').trim();
    if (!raw || raw[0] === '#' || /^(?:mailto:|tel:|sms:|javascript:)/i.test(raw)) return null;

    const url = sameOriginUrl(anchor.href);
    if (!url) return null;
    if (url.pathname === location.pathname && url.search === location.search) return null;
    if (/\/(?:cart|checkout)(?:\/|$)/i.test(url.pathname)) return null;
    if (/\.(?:pdf|zip|mp4|mov|webm|jpg|jpeg|png|webp|avif)(?:$|\?)/i.test(url.pathname + url.search)) return null;
    return url;
  }

  function prefetch(anchor) {
    if (constrained || prefetched.size >= MAX_PREFETCH || document.visibilityState !== 'visible') return;
    const url = destination(anchor);
    if (!url) return;

    url.hash = '';
    const key = url.href;
    if (key === location.href.split('#')[0] || prefetched.has(key)) return;

    prefetched.add(key);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = key;
    link.as = 'document';
    link.fetchPriority = 'low';
    link.dataset.pyPrefetch = '';
    document.head.appendChild(link);
  }

  function schedulePrefetch(anchor) {
    if (constrained || !destination(anchor)) return;
    if (hoverTimer) clearTimeout(hoverTimer);
    hoverAnchor = anchor;
    hoverTimer = setTimeout(() => {
      hoverTimer = 0;
      if (hoverAnchor === anchor) prefetch(anchor);
    }, HOVER_DELAY);
  }

  document.addEventListener('pointerover', event => {
    const anchor = getAnchor(event.target);
    if (anchor) schedulePrefetch(anchor);
  }, { passive: true });

  document.addEventListener('pointerout', event => {
    const anchor = getAnchor(event.target);
    if (anchor && hoverAnchor === anchor && !anchor.contains(event.relatedTarget)) {
      hoverAnchor = null;
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        hoverTimer = 0;
      }
    }
  }, { passive: true });

  document.addEventListener('focusin', event => {
    const anchor = getAnchor(event.target);
    if (anchor) schedulePrefetch(anchor);
  }, { passive: true });

  document.addEventListener('touchstart', event => {
    const anchor = getAnchor(event.target);
    if (anchor) prefetch(anchor);
  }, { passive: true });

  // Normal internal links navigate natively and immediately.
  // We only mark the next page so it can fade in.
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = getAnchor(event.target);
    if (destination(anchor)) {
      armProductTransition(anchor);
      markNavigation();
    }
  }, true);

  // Native forms that cause page navigation also only mark; never delay.
  document.addEventListener('submit', event => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = sameOriginUrl(form.action || location.href);
    if (!action) return;

    const method = (form.method || 'get').toLowerCase();
    const isLocalization = form.classList.contains('localization-form') || /\/localization(?:\/|$)/.test(action.pathname);
    if (isLocalization || method === 'get') markNavigation();
  }, true);

  let arrived = false;
  try {
    arrived = sessionStorage.getItem(KEY) === '1';
    if (arrived) sessionStorage.removeItem(KEY);
  } catch {}

  if (arrived && !nativeTransition && !reducedMotion.matches) {
    document.documentElement.classList.add('py-page-arrive');
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.documentElement.classList.remove('py-page-arrive-pending');
      setTimeout(() => document.documentElement.classList.remove('py-page-arrive'), 180);
    }));
  } else {
    document.documentElement.classList.remove('py-page-arrive-pending');
  }

  addEventListener('pageshow', event => {
    if (event.persisted) {
      document.documentElement.classList.remove('py-page-arrive-pending','py-page-arrive');
    }
  }, { passive: true });

  addEventListener('pagehide', () => {
    if (hoverTimer) clearTimeout(hoverTimer);
  }, { passive: true });

  window.pyPageTransition = { prefetch, navigate, submitForm };
})();